// TableView — the pure, presentational render of the Table block model.
// ISP-4018 (Fable Hail Mary Wave 5). NO craft.js / redux import, so both the
// builder node (Table.jsx) and any display renderer (view-wheel) reuse the
// IDENTICAL accessible markup, and unit tests can render it in isolation.
//
// Deck display rule (concepts/builder-blocks/02-table-block-editor): 4 rows show
// in the Segment; longer tables scroll in-place with the header row PINNED
// (position: sticky thead). Many columns scroll horizontally — the page body
// never overflows. Numeric columns right-align. Header text auto-flips
// white/navy by header-bar luminance so it always clears WCAG AA.

import React from "react";
import wiring from "./wiring";

const ROW_H = 44; // approx row height used to size the 4-row scroll window
const INK = "#123051";
const WHITE = "#FFFFFF";
// T22.542: AA fallback for a mid-tone header bar (e.g. AI palette green #6A994E)
// where neither navy (4.01:1) nor white (3.34:1) reaches 4.5:1. Black always
// does whenever white does not -- same rule as TitleBar / Accordion headers.
const BLACK = "#000000";

// WCAG relative luminance → pick a readable header-text color (AA auto-flip,
// the app's Segment-icon luminance rule). White on a dark bar, navy on a light one,
// black on a mid-tone bar where neither reaches AA 4.5:1.
export function readableOn(bg) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(bg || "").trim());
  if (!m) return WHITE;
  const hex = m[1];
  const ch = (i) => {
    const c = parseInt(hex.substr(i, 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4);
  const cWhite = (1.0 + 0.05) / (L + 0.05);
  const cInk = (L + 0.05) / (0.032 + 0.05); // ink #123051 luminance ~= 0.032
  if (Math.max(cWhite, cInk) < 4.5) return BLACK;
  return cWhite >= cInk ? WHITE : INK;
}

export const fontStack = (name) => {
  switch (name) {
    case "Arial": return "Arial, Helvetica, sans-serif";
    case "Georgia": return "Georgia, serif";
    case "Impact": return "Impact, Charcoal, sans-serif";
    case "Tahoma": return "Tahoma, Geneva, sans-serif";
    case "Times New Roman": return "'Times New Roman', Times, serif";
    case "Verdana": return "Verdana, Geneva, sans-serif";
    case "Poppins": return "'Poppins', sans-serif";
    case "Quando": return "'Quando', serif";
    default: return "'DM Sans', sans-serif";
  }
};

// fix-kelly-0930-P0 (Kelly, VP Sales): a newly-added Table starts EMPTY (see
// wiring.js's EMPTY default) -- render a clean hint instead of a table grid
// of blank cells, mirroring GraphChart.jsx's existing "Add data points to
// see your chart" empty state. A table is "blank" when every header cell and
// every row cell is empty/whitespace, regardless of how it got that way (a
// fresh block, or a user who cleared everything out).
const isBlankTable = (v) =>
  v.header.every((h) => !String(h || "").trim()) &&
  v.rows.every((row) => row.every((c) => !String(c || "").trim()));

const TableEmptyHint = () => (
  <div
    data-testid="table-empty-hint"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: ROW_H * 2,
      width: "100%",
      boxSizing: "border-box",
      padding: "16px",
      border: "1px dashed #D6BBFB",
      borderRadius: 8,
      backgroundColor: "#F9F5FF",
      color: "#667085",
      fontFamily: "'DM Sans', sans-serif",
      fontSize: 13,
      fontWeight: 500,
      textAlign: "center",
    }}
  >
    Add data to see your table
  </div>
);

const TableView = ({ value }) => {
  const v = wiring.normalize(value);
  const headerText = readableOn(v.headerColor);
  const maxHeight = ROW_H * (v.visibleRows + (v.showHeader ? 1 : 0)) + 2;

  if (isBlankTable(v)) {
    return <TableEmptyHint />;
  }

  return (
    <div
      data-testid="table-scroll"
      style={{
        maxHeight,
        overflow: "auto",
        border: "1px solid #E4E7EC",
        borderRadius: 8,
        WebkitOverflowScrolling: "touch",
      }}
    >
      <table
        data-testid="table-el"
        style={{
          borderCollapse: "collapse",
          width: "100%",
          minWidth: v.header.length * 96,
          fontFamily: fontStack(v.font),
          fontSize: 14,
          color: v.fontColor,
          tableLayout: "auto",
        }}
      >
        {v.caption ? (
          <caption
            style={{
              captionSide: "top",
              textAlign: "left",
              padding: "6px 10px",
              fontSize: 12,
              fontWeight: 600,
              color: "#667085",
            }}
          >
            {v.caption}
          </caption>
        ) : (
          <caption
            style={{
              position: "absolute",
              width: 1, height: 1, overflow: "hidden",
              clip: "rect(0 0 0 0)", clipPath: "inset(50%)", whiteSpace: "nowrap",
            }}
          >
            Data table
          </caption>
        )}
        {v.showHeader && (
          <thead>
            <tr>
              {v.header.map((h, c) => (
                <th
                  key={`th-${c}`}
                  scope="col"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    background: v.headerColor,
                    color: headerText,
                    textAlign: v.aligns[c] || "left",
                    fontWeight: 700,
                    padding: "10px 12px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {v.rows.map((row, r) => (
            <tr
              key={`tr-${r}`}
              style={{ background: r % 2 === 1 ? "#F7F5FF" : "#FFFFFF" }}
            >
              {row.map((cell, c) => (
                <td
                  key={`td-${r}-${c}`}
                  style={{
                    textAlign: v.aligns[c] || "left",
                    padding: "10px 12px",
                    borderTop: "1px solid #EEF0F4",
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableView;
